import { useEffect } from "react";
import { X, Play, Film } from "lucide-react";
import { platformLabel } from "@/lib/normalizeUrl";

// Selected Work leak fix (2026-09): mirrors the Agency Portal's already-correct in-portal
// viewer (pages/agency/WorkViewer.jsx, built for the "1b" tradeoff) so every OTHER surface that
// renders a `_public_work_sample_view` row (Discover, the freelancer Members directory,
// Preferred Crew / Partner Discover, Collaboration applications) stops linking straight to the
// raw YouTube/Vimeo url. Video samples play via the locked-down nocookie/dnt `embed_url`;
// anything un-embeddable falls back to an image lightbox, or a plain placeholder if there's no
// thumbnail either. No raw url, handle, channel/uploader identity, or platform name is ever
// rendered for a VIDEO sample — clicking a video sample never leaves NJM's app.
//
// Collaboration UX Wave 1 (2026-09): the rule above was written for videos (hide the raw
// YouTube/Vimeo url — a privacy concern, since it can reveal a channel/uploader identity) and
// was WRONGLY applied to every sample type, including valid non-video work (Drive, Docs,
// Behance, personal websites) that were never a privacy concern in the first place — those have
// no embed to fall back to, so they hit the exact same "available on request" dead end as an
// unembeddable private video, with no way to actually open the freelancer's own link. Confirmed
// live (A S Aravind's `asaravind.in/portfolio` — a public website, not malformed, not a
// permissions issue). Fixed below: non-video samples now render a real "Open work ↗" link.
export function WorkSampleLightbox({ sample, onClose }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  if (!sample) return null;
  const isVideo = sample.media_kind === "video";
  const isEmbeddable = isVideo && sample.embed_url;
  return (
    <div onClick={onClose} data-testid="work-sample-lightbox"
      className="fixed inset-0 z-[60] bg-black/85 flex items-center justify-center p-6">
      <div onClick={(e) => e.stopPropagation()} className="w-[min(920px,94vw)] max-h-[90vh]">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 min-w-0">
            {sample.role_tag && <span className="rounded-full bg-brand px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-ink">{sample.role_tag}</span>}
          </div>
          <button onClick={onClose} data-testid="work-sample-lightbox-close" aria-label="Close"
            className="text-white/70 hover:text-white transition-colors"><X className="h-5.5 w-5.5" /></button>
        </div>
        {isEmbeddable ? (
          <div className="relative pt-[56.25%] bg-black rounded-lg overflow-hidden">
            <iframe title="Selected work" src={sample.embed_url} data-testid="work-sample-lightbox-iframe"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
              referrerPolicy="strict-origin-when-cross-origin" allowFullScreen
              className="absolute inset-0 w-full h-full border-0" />
          </div>
        ) : sample.thumbnail_url ? (
          <img src={sample.thumbnail_url} alt="" data-testid="work-sample-lightbox-image"
            className="w-full max-h-[80vh] object-contain bg-[#111] rounded-lg" />
        ) : (
          <div data-testid="work-sample-lightbox-image" className="bg-[#111] rounded-lg px-6 py-16 text-center">
            <Film className="h-10 w-10 text-white/30 mx-auto mb-3.5" />
            <div className="text-white/85 text-[15px]">{sample.role_tag || sample._fallbackLabel || "Selected work"}</div>
          </div>
        )}
        {isVideo && !isEmbeddable && (
          <p className="text-white/55 text-xs mt-2.5 text-center">Full showreel available on request through your NJM producer.</p>
        )}
        {!isVideo && sample.url && (
          <div className="mt-3.5 flex flex-col items-center gap-1">
            <div className="text-white/85 text-[14px] font-medium">{sample.title && sample.title !== sample.url ? sample.title : platformLabel(sample.url, sample.platform)}</div>
            <div className="text-white/45 text-[11px]">{platformLabel(sample.url, sample.platform)}</div>
            <a href={sample.url} target="_blank" rel="noopener noreferrer" data-testid="work-sample-lightbox-open-link"
              className="mt-1.5 text-brand text-[12.5px] font-semibold hover:underline">Open work ↗</a>
          </div>
        )}
      </div>
    </div>
  );
}

// Clickable thumbnail — a button (never an anchor), so there's no href to leak and no
// target="_blank" navigation away from the app. `label` is shown only as a caption fallback
// (e.g. the freelancer's role), never the platform name or url.
export function WorkSampleThumb({ sample, onOpen, label, className = "aspect-video", showCaption = false, testId = "work-sample-thumb" }) {
  const isVideo = sample.media_kind === "video" && sample.embed_url;
  const caption = sample.role_tag || label;
  return (
    <button type="button" onClick={() => onOpen({ ...sample, _fallbackLabel: label })} data-testid={testId}
      className={`group relative block w-full text-left rounded-md overflow-hidden border border-hairline bg-ink/5 ${className}`}>
      {sample.thumbnail_url
        ? <img src={sample.thumbnail_url} alt={sample.title || ""} className="w-full h-full object-cover" loading="lazy" decoding="async" />
        : <span className="absolute inset-0 flex items-center justify-center"><Film className="h-5 w-5 text-faint" /></span>}
      {isVideo && (
        <span className="absolute inset-0 grid place-items-center bg-black/25 group-hover:bg-black/35 transition-colors">
          <span className="grid place-items-center h-7 w-7 rounded-full bg-white/92">
            <Play className="h-3.5 w-3.5 text-ink ml-0.5" fill="currentColor" />
          </span>
        </span>
      )}
      {showCaption && caption && (
        <span className="absolute bottom-0 inset-x-0 truncate bg-black/55 px-2 py-1 text-[10px] font-semibold text-white">{caption}</span>
      )}
    </button>
  );
}
